import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ConstructionSiteModel } from 'src/app/models/archive/construction-site.model';
import { FreightModel } from 'src/app/models/management/freight.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { FreightConstructionSiteTableModel } from 'src/app/models/tables/management/freight-construction-site-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { FreightService } from 'src/app/services/management/freight.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-freight-modal',
  templateUrl: './freight-modal.component.html',
  styleUrls: ['./freight-modal.component.scss'],
  standalone: false
})
export class FreightModalComponent implements OnInit {

  ascendant: boolean = true;
  cityDestiny: string = '';
  cityOrigin: string = '';
  columnSort: string | null = null;
  constructionSiteContract: ConstructionSiteModel = new ConstructionSiteModel();
  data: PagedResponseModel<FreightConstructionSiteTableModel> =
    new PagedResponseModel<FreightConstructionSiteTableModel>();
  departmentDestiny: string = '';
  departmentOrigin: string = '';
  disabledForm: boolean = false;
  formFreight: FormGroup = new FormGroup({});
  freightContract: FreightModel = new FreightModel();
  freightId: number = 0;
  header: ColumnResponseModel[] = [];
  limit: number = 10;
  listConcepts: any[] = [];
  page: number = 1;
  permissionUpdatable: boolean = false;
  sectorCodeName: string = '';
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    }
  ];

  constructor(private confirmDialogService: ConfirmDialogService,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private freightService: FreightService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new FreightModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.sectorCodeName = inputs.sectorCodeName;
      this.cityOrigin = inputs.cityCodeNameOrigin;
      this.cityDestiny = inputs.cityCodeNameDestiny;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.departmentOrigin = inputs.departmentCodeNameOrigin;
      this.departmentDestiny = inputs.departmentCodeNameDestiny;
      this.constructionSiteContract = inputs.constructionSiteContract;
    });
    await this.initializeFreightData();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  buildFreightModel(): FreightModel {
    const formValues = this.formFreight.getRawValue();
    const constructionSite = this.constructionSiteContract;

    return {
      ...formValues,
      sectorId: constructionSite.sectorId,
      cityOriginId: constructionSite.originCityId,
      cityDestinyId: constructionSite.destinityCityId,
      departmentOriginId: constructionSite.originDepartmentId,
      departmentDestinyId: constructionSite.destinityDepartmentId,
      constructionSiteId: constructionSite.constructionSiteId
    };
  }

  async createNewRegister() {
    this.freightId = 0;
    this.freightContract = new FreightModel();
    this.validateForm(this.freightContract);
  }

  async createFreight(modelFreight: FreightModel) {
    try {
      const response = await firstValueFrom(this.freightService.createFreight(modelFreight));
      this.freightId = response.freightId;
      this.freightContract = response;
      this.validateForm(this.freightContract);
      this.createNewRegister();
      this.toastHandlerService.showToast('success', `El flete fue creado exitosamente.`);
    } catch (error) {
      console.error('Error al crear el flete.', error);
    }
  }

  async deleteFreight(freightId: number) {
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el flete?')) {
      try {
        await firstValueFrom(this.freightService.deleteFreight(freightId));
        await this.getFreightByConstructionSite();
        this.toastHandlerService.showToast('success', 'El flete fue eliminado exitosamente.');
      } catch (error) {
        console.error('Error al eliminar el flete.', error);
      }
    }
  }

  async getConcept(conceptId: number) {
    await this.getFreightByKeys(conceptId);
  }

  async getFreightById(freightId: number) {
    try {
      const response = await firstValueFrom(this.freightService.getFreightById(freightId));
      this.freightId = response.freightId;
      this.freightContract = response;
      this.validateForm(this.freightContract);
    } catch (error) {
      console.error('Error al obtener el flete por id.', error);
    }
  }

  async getFreightByConstructionSite() {
    try {
      const constructionSiteId = this.constructionSiteContract.constructionSiteId;
      const response = await firstValueFrom(this.freightService.getFreightByConstructionSite(constructionSiteId,
        this.page, this.limit, this.columnSort, this.ascendant));
      this.data = response;
    } catch (error) {
      console.error('Error al buscar los fletes de la obra.', error);
    }
  }

  async getFreightByKeys(conceptId: number) {
    try {
      const { originCityId, destinityCityId, sectorId, constructionSiteId } = this.constructionSiteContract;
      const response = await firstValueFrom(this.freightService.getFreightByKeys(originCityId, destinityCityId,
        sectorId, conceptId, constructionSiteId));
      this.freightId = response.freightId;
      this.freightContract = response;
      this.validateForm(this.freightContract);
    } catch (error) {
      console.error('Error al consultar el flete por claves.', error);
    }
  }

  async getFreightColumnsForConstructionSite() {
    try {
      if (this.header.length === 0) {
        const response = await firstValueFrom(this.freightService.getFreightColumnsForConstructionSite());
        this.header = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta de fletes de la obra.', error);
    }
  }

  async getListConcepts() {
    try {
      const response = await firstValueFrom(this.freightService.getAllConceptsForSelect());
      this.listConcepts = response;
    } catch (error) {
      console.error('Error al obtener la lista de conceptos.', error);
    }
  }

  async initializeFreightData() {
    this.updateFormPermision();
    await Promise.all([
      this.getListConcepts(),
      this.getFreightByConstructionSite(),
      this.getFreightColumnsForConstructionSite()
    ]);
  }

  async onAction(event: {
    action: string, item?: FreightConstructionSiteTableModel,
    selectedItems?: number[]
  }) {
    if (event.action === 'edit' && event.item) {
      await this.getFreightById(event.item.id);
    }

    if (event.action === 'delete' && event.item) {
      await this.deleteFreight(event.item.id);
    }
  }

  async onPageChange(event: {
    page: number; pageSize: number,
    sortField: string | undefined, sortOrder: number
  }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getFreightByConstructionSite();
  }

  async send() {
    this.validateFormControls();
    if (!this.formFreight.valid) {
      this.showValidationMessages();
      return;
    }
    const modelFreight = this.buildFreightModel();
    if (this.freightId == 0) {
      await this.createFreight(modelFreight);
    } else {
      await this.updateFreight(modelFreight);
    }
    await this.getFreightByConstructionSite();
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      conceptId: {
        required: 'El Concepto es obligatorio.',
        min: 'Debe seleccionar un Concepto válido.'
      },
      value: {
        min: 'El Valor no puede ser negativo.',
        max: 'El Valor no puede superar 99.999.999.999,99.'
      },
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formFreight.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateFreight(modelFreight: FreightModel) {
    this.freightContract.value = modelFreight.value;
    this.freightContract.conceptId = modelFreight.conceptId;
    try {
      const response = await firstValueFrom(this.freightService.updateFreight(this.freightContract));
      this.freightContract = response;
      this.validateForm(this.freightContract);
      this.createNewRegister();
      this.toastHandlerService.showToast('success', `El flete fue modificado exitosamente.`);
    } catch (error) {
      console.error('Error al actualizar el flete.', error);
    }
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formFreight.enable());
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formFreight.disable());
    }
  }

  validateForm(freight: FreightModel) {
    this.formFreight = this.formBuilder.group({
      conceptId: [freight.conceptId, [Validators.required, Validators.min(1)]],
      value: [freight.value, [Validators.min(0), Validators.max(99999999999.99)]],
    });
  }

  validateFormControls() {
    Object.keys(this.formFreight.controls).forEach((key) => {
      const control = this.formFreight.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
